import axios from 'axios';

const BASE = '/api/v1/manual-collections/deficit-details';

export interface DeficitDetail {
  id: string;
  riderId: string;
  publicRiderId: string;
  totalRentAmount: number;
  totalRentPaid: number;
  channelAmount: number;
  totalChannelPaid: number;
  damageCost: number;
  totalDamagePaid: number;
  weekStartDate: string | null;
  weekEndDate: string | null;
  updatedAt: string;
  updatedBy: string;
  rider: {
    id: string;
    name: string;
    phone: string;
    city: string;
    publicRiderId: string;
  };
}

export interface LatestRentUpdateDates {
  weekStartDate: string | null;
  weekEndDate: string | null;
}

export interface DeficitDetailsResponse {
  success: boolean;
  data: DeficitDetail[] | DeficitDetail | null;
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface DeficitTransaction {
  id: string;
  riderId: string;
  publicRiderId: string | null;
  rentAmount: number;
  beforeRentAmount: number;
  afterRentAmount: number;
  damageAmount: number;
  beforeDamageAmount: number;
  afterDamageAmount: number;
  channelAmount: number;
  beforeChannelAmount: number;
  afterChannelAmount: number;
  updatedBy: string | null;
  updatedOn: string;
  rider: {
    id: string;
    name: string;
    phone: string;
    city: string;
  };
}

export interface DeficitTransactionsResponse {
  success: boolean;
  data: DeficitTransaction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface UpdateDeficitDetailsPayload {
  publicRiderId: string;
  totalRentAmount: number;
  channelAmount: number;
  damageCost: number;
  weekStartDate?: string | null;
  weekEndDate?: string | null;
}

export interface BulkDeficitUpdateRow {
  publicRiderId: string;
  totalRentAmount: number;
  channelAmount: number;
  damageCost: number;
}

export interface BulkDeficitUpdateResponse {
  success: boolean;
  message: string;
  summary: {
    totalRows: number;
    successCount: number;
    failureCount: number;
  };
  successes: Array<{ rowNumber: number; publicRiderId: string; deductedAmount: number }>;
  failures: Array<{ rowNumber: number; publicRiderId: string; message: string }>;
}

export interface BulkDeficitIncreaseRow extends UpdateDeficitDetailsPayload {
  weekStartDate: string | null;
  weekEndDate: string | null;
}

export interface BulkDeficitIncreaseResponse {
  success: boolean;
  message: string;
  summary: {
    totalRows: number;
    successCount: number;
    failureCount: number;
    warningCount: number;
  };
  successes: Array<{ rowNumber: number; publicRiderId: string; addedAmount: number }>;
  failures: Array<{ rowNumber: number; publicRiderId: string; message: string }>;
  warnings: Array<{ rowNumber: number; publicRiderId: string; message: string }>;
}

const authHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getDeficitDetails = async (params: {
  page?: number;
  limit?: number;
  publicRiderId?: string;
  riderId?: string;
}): Promise<DeficitDetailsResponse> => {
  const res = await axios.get(BASE, { params, headers: authHeaders() });
  return res.data;
};

const getDeficitTransactions = async (params?: {
  page?: number;
  limit?: number;
  publicRiderId?: string;
  riderId?: string;
}): Promise<DeficitTransactionsResponse> => {
  const res = await axios.get('/api/v1/manual-collections/deficit-transactions', {
    params,
    headers: authHeaders(),
  });
  return res.data;
};

const getLatestRentUpdateDates = async (
  publicRiderId: string,
): Promise<LatestRentUpdateDates> => {
  const res = await axios.get(
    `/api/rent/latest-update-items/${encodeURIComponent(publicRiderId)}`,
    { headers: authHeaders() },
  );
  return res.data.data;
};

const exportDeficitDetails = async (params?: { city?: string; fromDate?: string; toDate?: string }) => {
  const res = await axios.get(`${BASE}/export`, {
    params,
    headers: authHeaders(),
    responseType: 'arraybuffer',
  });
  return res;
};

const updateDeficitDetails = async (payload: UpdateDeficitDetailsPayload) => {
  const res = await axios.post(BASE, payload, { headers: authHeaders() });
  return res.data;
};

const increaseDeficitDetails = async (payload: UpdateDeficitDetailsPayload) => {
  const res = await axios.post(`${BASE}/increase`, payload, { headers: authHeaders() });
  return res.data;
};

const bulkIncreaseDeficitDetails = async (rows: BulkDeficitIncreaseRow[]): Promise<BulkDeficitIncreaseResponse> => {
  const res = await axios.post(`${BASE}/increase/bulk`, { rows }, { headers: authHeaders() });
  return res.data;
};

const updateExistingDeficitDetails = async (payload: UpdateDeficitDetailsPayload) => {
  const { publicRiderId, ...amounts } = payload;
  const res = await axios.patch(`${BASE}/${encodeURIComponent(publicRiderId)}`, amounts, { headers: authHeaders() });
  return res.data;
};

const bulkUpdateDeficitDetails = async (rows: BulkDeficitUpdateRow[]): Promise<BulkDeficitUpdateResponse> => {
  const res = await axios.post(`${BASE}/bulk`, { rows }, { headers: authHeaders() });
  return res.data;
};

const deficitDetailsService = {
  getDeficitDetails,
  getDeficitTransactions,
  getLatestRentUpdateDates,
  exportDeficitDetails,
  updateDeficitDetails,
  increaseDeficitDetails,
  bulkIncreaseDeficitDetails,
  updateExistingDeficitDetails,
  bulkUpdateDeficitDetails,
};

export default deficitDetailsService;
